import { Select } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Book, TestBank } from 'model/response';
import Table, { ColumnsType } from 'antd/es/table';
import { searchBook, toggleBookWebviewActive } from '../../../../service/bookService';
import { useMasterGradesSelector, useMasterSubjectsSelector } from '../../../../redux/selector';
import processError from '../../../../utils/processError';
import { mapToSelectItems } from '../../../../utils/utils';
import BookFormDrawer from '../BookFormDrawer';
import { findAllTestBank } from '../../../../service/testBankService';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../../redux/store/store';
import { updateLoading } from '../../../../redux/slice/appConfigSlice';
import WebViewActiveCheckbox from '../../../common/WebViewActiveCheckbox';
import useTableConfig from '../../../../hooks/useTableConfig';
import Search from 'antd/es/input/Search';

interface BookFormDrawerData {
  open: boolean;
  defaultBook: Book | null;
}

export default function BookManagementTable() {
  const [loading, setLoading] = useState(false);
  const { pageSize, currentPage, sortBy, direction, pagination, setTotalElements, getColumnSorter, handleTableChange } =
    useTableConfig();
  const [data, setData] = useState<Book[]>([]);
  const [bookFormDrawerData, setBookFormDrawerData] = useState<BookFormDrawerData>({
    open: true,
    defaultBook: null,
  });
  const [testBankId, setTestBankId] = useState<string | null>(null);
  const [subjectId, setSubjectId] = useState<string | null>(null);
  const [gradeId, setGradeId] = useState<string | null>(null);
  const [keyword, setKeyword] = useState('');
  const subjects = useMasterSubjectsSelector();
  const grades = useMasterGradesSelector();
  const [testBanks, setTestBanks] = useState<TestBank[]>([]);
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    findAllTestBank().then((response) => setTestBanks(response));
  }, []);

  useEffect(() => {
    dispatch(updateLoading(true));
    setLoading(true);
    const timeoutId = setTimeout(() => {
      setBookFormDrawerData({
        open: false,
        defaultBook: null,
      });
      dispatch(updateLoading(false));
    }, 200);

    return () => {
      clearTimeout(timeoutId);
      dispatch(updateLoading(false));
    };
  }, [dispatch]);

  const getSubjectName = useCallback(
    (subjectId: string | undefined) => {
      if (!subjectId) return '';
      return subjects.find((subject) => subject.id === subjectId)?.name || '';
    },
    [subjects],
  );

  const getGradeName = useCallback(
    (gradeId: string | undefined) => {
      if (!gradeId) return '';
      return grades.find((grade) => grade.id === gradeId)?.name || '';
    },
    [grades],
  );

  const columns: ColumnsType<Book> = [
    {
      title: 'Tên sách',
      dataIndex: 'name',
      align: 'left',
      render: (_, book) => <Link to={`/electronic-book/book/${book.id}`}>{book.name}</Link>,
      sorter: true,
      sortOrder: getColumnSorter('name'),
    },
    {
      title: 'ID sách',
      dataIndex: 'code',
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('code'),
    },
    {
      title: 'Môn học',
      dataIndex: 'subject.id',
      align: 'left',
      render: (_, book) => <div>{getSubjectName(book?.subject?.id)}</div>,
      sorter: true,
      sortOrder: getColumnSorter('subject.id'),
    },
    {
      title: 'Lớp học',
      dataIndex: 'grades',
      align: 'left',
      render: (_, book) => <div>{book.grades.map((grade) => getGradeName(grade.id)).join(', ')}</div>,
    },
    {
      title: 'Hiển thị',
      dataIndex: 'webViewActive',
      align: 'center',
      render: (_, book) => (
        <WebViewActiveCheckbox
          currentActiveValue={book.webViewActive}
          itemName={'sách'}
          setLoading={setLoading}
          toggleWebviewActive={async () => toggleBookWebviewActive(book.id)}
          reloadPageData={loadListBooks}
        />
      ),
      sorter: true,
      sortOrder: getColumnSorter('webViewActive'),
    },
    {
      title: 'Thứ tự',
      dataIndex: 'webViewOrder',
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('webViewOrder'),
    },
    {
      title: 'Hành động',
      dataIndex: 'action',
      align: 'center',
      render: (_, record) => (
        <div className={'flex gap-x-1 justify-center'}>
          <button
            className="p-2 rounded bg-[#0066FF] text-white"
            onClick={() => {
              setBookFormDrawerData({
                open: true,
                defaultBook: record,
              });
            }}
          >
            <EditOutlined />
          </button>
        </div>
      ),
    },
  ];

  useEffect(() => {
    loadListBooks().then().catch(processError);
  }, [currentPage, pageSize, sortBy, direction, subjectId, gradeId, keyword]);

  const loadListBooks = async () => {
    setLoading(true);
    const bookPage = await searchBook(
      pageSize,
      currentPage,
      sortBy,
      direction,
      keyword,
      subjectId,
      gradeId,
      testBankId,
    );
    setData(bookPage.content);
    setLoading(false);
    setTotalElements(bookPage.totalElements);
  };

  async function handleBookFormBeforeSuccess() {
    setBookFormDrawerData({
      open: false,
      defaultBook: null,
    });
    await loadListBooks();
  }

  return (
    <div className={'h-[calc(100vh-112px)] overflow-y-auto'}>
      <div className="flex flex-col gap-4">
        <div className="p-2 flex items-center justify-between bg-white rounded w-full">
          <div className="flex gap-x-4">
            <button
              className="py-1 bg-[#cdf1ff] hover:bg-[#bbe7fa] flex items-center rounded px-2 w-max"
              onClick={() =>
                setBookFormDrawerData({
                  open: true,
                  defaultBook: null,
                })
              }
            >
              <PlusOutlined className="text-[15px]" />
              <span className="pl-2 inline-block w-max">Thêm mới sách</span>
            </button>
            <Select
              onChange={(e) => setSubjectId(e)}
              className="w-full min-w-[240px]"
              placeholder="Bộ lọc - Môn học"
              style={{ flex: 1 }}
              value={subjectId}
              options={[
                {
                  value: '',
                  label: 'Tất cả',
                },
              ].concat(mapToSelectItems(subjects))}
            />
            <Select
              onChange={(e) => setGradeId(e)}
              className="w-full min-w-[180px]"
              placeholder="Bộ lọc - Lớp"
              value={gradeId}
              options={[
                {
                  value: '',
                  label: 'Tất cả',
                },
              ].concat(mapToSelectItems(grades))}
            />
            <Select
              onChange={(e) => setTestBankId(e)}
              className="w-full min-w-[180px]"
              placeholder="Bộ lọc - Ngân hàng"
              value={testBankId}
              options={[
                {
                  value: '',
                  label: 'Tất cả',
                },
              ].concat(mapToSelectItems(testBanks))}
            />
          </div>
          <Search
            placeholder={'Nhập từ khóa tìm kiếm'}
            rootClassName={'h-9 rounded-3xl border border-solid border-f46055 flex items-center overflow-hidden w-fit'}
            onSearch={setKeyword}
          />
        </div>
      </div>
      <div>
        <div className="flex flex-col">
          <Table
            columns={columns}
            dataSource={data}
            rowKey={(record) => record.id}
            loading={loading}
            pagination={pagination}
            onChange={handleTableChange}
            bordered
          />
        </div>
      </div>
      <BookFormDrawer
        mode={bookFormDrawerData.defaultBook ? 'edit' : 'create'}
        defaultBook={bookFormDrawerData.defaultBook}
        open={bookFormDrawerData.open}
        onBeforeSuccess={handleBookFormBeforeSuccess}
        onClose={() =>
          setBookFormDrawerData({
            open: false,
            defaultBook: null,
          })
        }
      />
    </div>
  );
}
